import type {
  Feature,
  FeatureEstimate,
  TaskProfile,
  TaskType,
  OverheadConfig,
  ModelPricing,
} from "./schemas.js";
import { mapTaskTypes } from "./task-mapper.js";
import { getComplexityMultiplier, getProfile, getModelPricing } from "./data-loader.js";
import { recommendModel } from "./recommender.js";
import type { LoadedData } from "./data-loader.js";

// ── Overhead calculation ────────────────────────────────────

interface OverheadBreakdown {
  contextResend: number;
  toolCalls: number;
  thinking: number;
  errorRecovery: number;
  planning: number;
  review: number;
}

function calculateOverhead(
  baseTotal: number,
  overhead: OverheadConfig,
  modelId: string,
): { multiplier: number; breakdown: OverheadBreakdown } {
  // Estimate ~5 turns for an average task
  const turns = 5;
  const contextResend = overhead.multi_turn_context_resend * turns;

  // Estimate ~10 tool calls
  const toolCallCount = 10;
  const toolCalls =
    (overhead.tool_call_overhead_input + overhead.tool_call_overhead_output) * toolCallCount;

  // Extended thinking based on model
  const thinking = modelId.includes("opus")
    ? overhead.extended_thinking_opus
    : modelId.includes("sonnet")
      ? overhead.extended_thinking_sonnet
      : 1.0;

  // Error recovery: chance × multiplier
  const errorRecovery = overhead.error_recovery_chance * overhead.error_recovery_multiplier;

  const planning = overhead.planning_pass;
  const review = overhead.review_pass;

  // Combined multiplier (additive overheads on top of thinking multiplier)
  const multiplier = thinking * (1 + contextResend / baseTotal + errorRecovery + planning + review);

  return {
    multiplier,
    breakdown: {
      contextResend,
      toolCalls,
      thinking,
      errorRecovery,
      planning,
      review,
    },
  };
}

// ── Cost calculation ────────────────────────────────────────

function calculateCost(
  inputTokens: number,
  outputTokens: number,
  pricing: ModelPricing,
): number {
  const inputCost = (inputTokens / 1_000_000) * pricing.input_per_mtok;
  const outputCost = (outputTokens / 1_000_000) * pricing.output_per_mtok;
  return Math.round((inputCost + outputCost) * 100) / 100;
}

// ── Main estimator ──────────────────────────────────────────

export function estimateFeature(feature: Feature, data: LoadedData): FeatureEstimate {
  const taskTypes = mapTaskTypes(feature);
  const complexityMult = getComplexityMultiplier(data.complexityMultipliers, feature.complexity);
  const recommended = recommendModel(feature.category, data.recommendations);
  const modelId = recommended.model;
  const pricing = getModelPricing(data.models, modelId) ?? data.models[0];

  // Sum base tokens across task types
  let baseInput = 0;
  let baseOutput = 0;

  for (const taskType of taskTypes) {
    const profile = getProfile(data.profiles, taskType);
    if (profile) {
      baseInput += profile.input;
      baseOutput += profile.output;
    }
  }

  // Apply complexity multiplier
  baseInput = Math.round(baseInput * complexityMult);
  baseOutput = Math.round(baseOutput * complexityMult);
  const baseTotal = baseInput + baseOutput;

  // Apply overhead
  const { multiplier, breakdown } = calculateOverhead(baseTotal, data.overhead, modelId);
  const overheadInput = Math.round(baseInput * multiplier + breakdown.toolCalls * 0.25);
  const overheadOutput = Math.round(baseOutput * multiplier + breakdown.toolCalls * 0.75);
  const overheadTotal = overheadInput + overheadOutput;

  const cost = calculateCost(overheadInput, overheadOutput, pricing);

  // Confidence intervals: 0.7x (low) / 1.0x (expected) / 1.5x (high)
  const confidence = {
    low: {
      tokens: Math.round(overheadTotal * 0.7),
      cost: Math.round(cost * 0.7 * 100) / 100,
    },
    expected: {
      tokens: overheadTotal,
      cost,
    },
    high: {
      tokens: Math.round(overheadTotal * 1.5),
      cost: Math.round(cost * 1.5 * 100) / 100,
    },
  };

  return {
    featureId: feature.id,
    featureName: feature.name,
    category: feature.category,
    complexity: feature.complexity,
    taskTypes,
    recommendedModel: modelId,
    baseTokens: { input: baseInput, output: baseOutput, total: baseTotal },
    withOverhead: { input: overheadInput, output: overheadOutput, total: overheadTotal },
    estimatedCost: cost,
    overheadBreakdown: breakdown,
    confidence,
  };
}

export function estimateAll(features: Feature[], data: LoadedData): FeatureEstimate[] {
  return features.map((f) => estimateFeature(f, data));
}
